import React, { useEffect, useState } from 'react';
import { FiShield, FiCopy, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { useFinance } from '../../contexts/FinanceContext';
import * as fs from './financeStyles';

// Wraps every finance page. Nothing under it renders until the current
// user's MFA session is fresh — mirroring the backend's FinanceMFARequired,
// which rejects every finance endpoint otherwise. Three states in order:
//   1. not enrolled  -> show provision + confirm (first-time setup)
//   2. enrolled, session stale -> show a plain "enter your code" prompt
//   3. session fresh -> render children
const FinanceMFAGate = ({ children }) => {
  const { mfaStatus, statusLoading, statusError, refreshMfaStatus, provisionDevice, confirmDevice, verifySession } = useFinance();
  const [provisioned, setProvisioned] = useState(null); // { secret, provisioning_uri }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mfaStatus === null) {
      refreshMfaStatus().catch(() => {});
    }
  }, [mfaStatus, refreshMfaStatus]);

  const handleProvision = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await provisionDevice();
      setProvisioned(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to start MFA setup.');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmDevice(code);
      setCode('');
      setProvisioned(null);
    } catch (e2) {
      setError(e2?.response?.data?.error || 'Invalid code — try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifySession(code);
      setCode('');
    } catch (e2) {
      setError(e2?.response?.data?.error || 'Invalid code — try again.');
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (!provisioned) return;
    navigator.clipboard?.writeText(provisioned.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (statusLoading && mfaStatus === null) {
    return <div style={fs.loadingState}>Checking finance security status…</div>;
  }

  if (statusError && mfaStatus === null) {
    return (
      <div style={fs.page}>
        <div style={fs.errorBanner}><FiAlertTriangle size={18} /><span>{statusError}</span></div>
      </div>
    );
  }

  if (mfaStatus && mfaStatus.enrolled && mfaStatus.session_mfa_fresh) {
    return children;
  }

  const gateCard = { ...fs.card, maxWidth: 420, margin: '80px auto 0' };

  // --- Not enrolled yet: first-time setup ---
  if (mfaStatus && !mfaStatus.enrolled) {
    return (
      <div style={fs.page}>
        <div style={gateCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <FiShield size={20} color={fs.colors.purple} />
            <h2 style={{ margin: 0, fontSize: 18, color: fs.colors.textBright }}>Set up finance security</h2>
          </div>
          <p style={{ color: fs.colors.textFaint, fontSize: 13, lineHeight: 1.6 }}>
            Financial data requires two-factor authentication. Set up an authenticator app
            (Google Authenticator, Authy, 1Password, etc.) once — you'll enter a fresh code each time you access finance.
          </p>

          {error && <div style={{ ...fs.errorBanner, marginBottom: 14 }}><FiAlertTriangle size={16} /><span>{error}</span></div>}

          {!provisioned ? (
            <button onClick={handleProvision} disabled={busy} style={fs.primaryButton}>
              {busy ? 'Starting…' : 'Start Setup'}
            </button>
          ) : (
            <form onSubmit={handleConfirm}>
              <div style={{ marginBottom: 14 }}>
                <div style={fs.label}>Manual entry key</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ ...fs.input, flex: 1, fontSize: 13, wordBreak: 'break-all' }}>{provisioned.secret}</code>
                  <button type="button" onClick={copySecret} style={fs.iconButton} title="Copy">
                    {copied ? <FiCheck size={15} color={fs.colors.green} /> : <FiCopy size={15} />}
                  </button>
                </div>
                <div style={{ color: fs.colors.textFaint, fontSize: 11, marginTop: 6 }}>
                  Add this key to your authenticator app (or paste the full URI below if it supports it).
                </div>
                <code style={{ display: 'block', marginTop: 8, fontSize: 10, color: fs.colors.textFaint, wordBreak: 'break-all' }}>
                  {provisioned.provisioning_uri}
                </code>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={fs.label}>Enter the 6-digit code from your app</div>
                <input
                  value={code} onChange={(e) => setCode(e.target.value)} maxLength={10} autoFocus
                  placeholder="000000" style={{ ...fs.input, width: '100%', fontSize: 18, letterSpacing: '0.2em', textAlign: 'center' }}
                />
              </div>
              <button type="submit" disabled={busy || !code} style={{ ...fs.primaryButton, width: '100%', justifyContent: 'center' }}>
                {busy ? 'Confirming…' : 'Confirm & Activate'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // --- Enrolled, but this session hasn't verified (or has gone stale) ---
  return (
    <div style={fs.page}>
      <div style={gateCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <FiShield size={20} color={fs.colors.purple} />
          <h2 style={{ margin: 0, fontSize: 18, color: fs.colors.textBright }}>Verify to continue</h2>
        </div>
        <p style={{ color: fs.colors.textFaint, fontSize: 13, lineHeight: 1.6 }}>
          Enter the current code from your authenticator app.
        </p>
        {error && <div style={{ ...fs.errorBanner, marginBottom: 14 }}><FiAlertTriangle size={16} /><span>{error}</span></div>}
        <form onSubmit={handleVerify}>
          <input
            value={code} onChange={(e) => setCode(e.target.value)} maxLength={10} autoFocus
            placeholder="000000" style={{ ...fs.input, width: '100%', fontSize: 18, letterSpacing: '0.2em', textAlign: 'center', marginBottom: 14 }}
          />
          <button type="submit" disabled={busy || !code} style={{ ...fs.primaryButton, width: '100%', justifyContent: 'center' }}>
            {busy ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FinanceMFAGate;
